import foundation from '../../bbjs/foundation.js';

import { Subscription, SubscriptionLike, ReplaySubject } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { QueryParameter } from '../interfaces/queryString';
import { Location } from '@angular/common';
import { TableSort } from './bb-table.service';

declare let window: any;

@Injectable()
export class LocationService implements OnDestroy {
	public paramsChangedObservable: ReplaySubject<QueryParameter[]>;
	public server: string;
	public host: string;

	private queryParameters: QueryParameter[] = null;
	private baseUrl = '';
	private lastParsedUrl = '';

	private environment: string;
	private publication: string;
	private globalLogin = false;
	private locationSubscription: SubscriptionLike;
	private routerSubscription: Subscription;

	private backOrForwardButtonUsed: boolean;

	constructor(private router: Router, private location: Location) {
		this.paramsChangedObservable = new ReplaySubject<QueryParameter[]>(1);

		const urlParts = this.location.path().split('?');
		this.baseUrl = urlParts[0];
		this.parseHost();

		this.locationSubscription = this.location.subscribe((popStateEvent: PopStateEvent) => {
			if (popStateEvent && popStateEvent.type === 'hashchange') {
				this.backOrForwardButtonUsed = true;
			}
			// firefox's order is different so we use a timeout
			setTimeout(() => {
				this.backOrForwardButtonUsed = false;
			}, 5);
		});

		this.routerSubscription = this.router.events.subscribe(event => {
			if (event instanceof NavigationEnd) {
				this.parseUrl();

				const nextBaseUrl = event.urlAfterRedirects.split('?')[0];
				if (this.baseUrl !== nextBaseUrl) {
					this.baseUrl = nextBaseUrl;
					this.updateUrl();
				}
				this.backOrForwardButtonUsed = false;
			}
		});

		this.backOrForwardButtonUsed = false;
	}

	reload() {
		window.location.reload();
	}

	getEnvironment(): string {
		return this.environment;
	}

	ngOnDestroy() {
		if (this.locationSubscription) {
			this.locationSubscription.unsubscribe();
		}
		if (this.routerSubscription) {
			this.routerSubscription.unsubscribe();
		}
	}

	getGlobalLogin(): boolean {
		return this.globalLogin;
	}

	getParams(): QueryParameter[] {
		return this.queryParameters;
	}

	removeParam(name: string) {

		const queryParameter = this.getParam(name);

		if (queryParameter) {
			const index = this.queryParameters.indexOf(queryParameter);
			this.queryParameters.splice(index, 1);

			this.updateUrl();
			this.paramsChangedObservable.next(this.queryParameters);
		}
	}

	getParam(name: string): QueryParameter {
		if (this.queryParameters) {
			for (const queryParameter of this.queryParameters) {
				if (queryParameter.name === name) {
					return queryParameter;
				}
			}
		}
	}

	setParam(name: string, values: string | number | string[]) {
		let prev = null;
		if (this.getParam(name) && this.getParam(name).values) {
			prev = this.getParam(name).values[0];
		}

		if (JSON.stringify(prev) !== JSON.stringify(values)) {
			this.setParameter(name, values);
			this.updateUrl();
			this.paramsChangedObservable.next(this.queryParameters);
		}
	}

	// applies set of key/value , or an array of QueryParameters, to the url, which should only trigger 1 new item on the browser history stack
	setParams(params: { [name: string]: string | number | string[] } | QueryParameter[], add = false) {
		if (typeof (params as any).length !== 'undefined') {

			// array of QueryParameter provided
			params = params as QueryParameter[];

			for (const queryParam of params) {
				this.setParameter(queryParam.name, queryParam.values, add);
			}
			this.updateUrl();
		} else {

			// key / value pairs provided
			for (const name of Object.keys(params)) {
				this.setParameter(name, params[name], add);
			}
			this.updateUrl();
		}

		this.paramsChangedObservable.next(this.queryParameters);
	}

	addParams(params: { [name: string]: string | number | string[] } | QueryParameter[]) {
		this.setParams(params, true);
	}

	addParam(name: string, values: string | number | string[]) {
		this.setParameter(name, values, true);
		this.paramsChangedObservable.next(this.queryParameters);
	}

	parseHost() {
		this.host = '';
		const host = location.hostname;
		const parts = host.split('.');
		const dtap = ['dev', 'test', 'acc'];
		// console.log('parse host');

		// override server from ?pub=foo parameter?
		const publicationOverride = this.getQueryVariable('pub');

		if (dtap.indexOf(parts[0]) > -1) {

			// dev.bbvms.com	 	// Global (dev)
			// acc.bbvms.com  		// Global (acc)
			// test.bbvms.com 		// Global (test)

			this.environment = parts[0];
			this.globalLogin = true;

		} else if (dtap.indexOf(parts[1]) > -1 && !location.port.startsWith('420')) {

			// bb.test.bbvms.com 	// Publication (test)
			// bb.acc.bbvms.com 	// Publication (acc)
			// bb.dev.bbvms.com 	// Publication (dev)

			this.environment = parts[1];
			this.publication = parts[0];

		} else if (host.indexOf('localhost') > -1 || location.port.startsWith('420')) {
			this.host = location.host;
			if (publicationOverride) {
				this.host = this.host + '?pub=' + publicationOverride;
			}
			if (host === 'localhost' || location.port.startsWith('420')) {
				// localhost 			// Global (local dev)

				this.environment = 'dev';
				this.globalLogin = true;

				if (parts.length === 5) {

					// bb.localhost	 	// Publication (local dev)

					this.environment = 'dev';
					this.publication = parts[0];

				}
			}
		} else {

			if (parts.length > 2) {
				// bb.bbvms.com	 	// Publication

				this.environment = 'prod';
				this.publication = parts[0];

			} else if (parts.length === 2) {

				// bbvms.com		 	// Global

				this.globalLogin = true;
				this.environment = 'prod';
			}
		}

		if (this.environment === 'dev' && !host.endsWith('local.dev.bbvms.com') && (host.endsWith('.bbvms.com') || host.startsWith('localhost'))) {
			this.server = 'https://' + (this.publication ? this.publication + '.' : '') + 'dev.bbvms.com';
		} else {
			this.host = location.host;
			this.server = 'https://' + host;
		}


		if (publicationOverride) {
			this.server = 'https://' + publicationOverride + '.bbvms.com';

			let testEnv;

			if (publicationOverride.indexOf('.') !== -1) {
				testEnv = publicationOverride.split('.')[1];
			} else {
				testEnv = 'prod';
			}

			this.environment = testEnv + ' with ' + this.environment;
		}

		this.preloadPlayer();
	}

	private parseUrl() {
		const url = this.location.path();

		if (url === this.lastParsedUrl) {
			return;
		}

		this.lastParsedUrl = url;

		const urlParts = url.split('?');

		const prevQueryParameters = this.queryParameters;

		this.queryParameters = [];

		if (urlParts[0] !== this.baseUrl) {
			this.baseUrl = urlParts[0];
		}


		if (typeof urlParts[1] !== 'undefined') {
			const params = urlParts[1].split('&');

			const hasSortProperty: boolean = urlParts[1].search('sortProperty') > -1;
			for (const param of params) {

				/* eslint-disable-next-line prefer-const */
				let [name, value] = param.split('=');
				value = decodeURIComponent(value);

				if (name === 'sortDirection') {
					// Only add sortDirection when sortProperty is set as well
					if (hasSortProperty) {
						this.setParameter(name, value, true);
					} else {
						this.location.replaceState(this.baseUrl, urlParts[1].replace(/sortDirection=\w+&?/, '').replace(/&$/, ''));
					}
				} else {
					this.setParameter(name, value, true);
				}
			}
		}

		if (prevQueryParameters !== this.queryParameters) {
			this.paramsChangedObservable.next(this.queryParameters);
		}
	}

	private setParameter(name: string, values: string | number | string[], add: boolean = false) {

		if (typeof values === 'number') {
			values = values.toString();
		}

		if (typeof values === 'string') {
			values = [values];
		}

		// console.warn('setParameter', name, values);

		const queryParameter = this.getParam(name);

		if (queryParameter) {
			if (!add) {
				queryParameter.values = [];
			}

			for (const value of values) {
				if (queryParameter.values.indexOf(value) === -1) {
					queryParameter.values.push(value);
				}
			}
		} else {
			this.queryParameters.push({ name, values } as QueryParameter);
		}
	}

	private updateUrl() {
		if (this.backOrForwardButtonUsed) {
			return;
		}

		const params: string[] = [];

		if (this.queryParameters) {
			const sort: TableSort = { sortProperty: null, sortDirection: null };
			for (const queryParameter of this.queryParameters) {
				if (typeof queryParameter.values !== 'undefined') {
					for (const value of queryParameter.values) {
						params.push(queryParameter.name + '=' + value);
					}
				}
				if (queryParameter.name === 'sortProperty' && queryParameter.values.length) {
					sort.sortProperty = queryParameter.values[0];
				}
				if (queryParameter.name === 'sortDirection' && queryParameter.values.length) {
					sort.sortDirection = queryParameter.values[0];
				}
			}
			if (sort.sortDirection && !sort.sortProperty) {
				sort.sortDirection = null;
				this.queryParameters = this.queryParameters
					.filter((queryParameter: QueryParameter) => queryParameter && queryParameter.name !== 'sortDirection');
			}
		}

		let url: string = this.baseUrl + (params && params.length ? '?' + params.join('&') : '');

		if (url === '/') {
			url = '/home';
		}

		this.router.navigateByUrl(url);
		this.lastParsedUrl = url;
	}

	private preloadPlayer(): void {
		const env = this.environment && this.environment.replace(/\s.*$/, ''); // only keep first part
		const cdn = 'cdn' + ((env && env !== 'prod') ? '-' + env : ''); // 'cdn-dev', 'cdn-acc', 'cdn-test', or 'cdn'
		const playerOpts = { playerPath: 'https://' + cdn + '.bluebillywig.com/apps/player/latest/' };
		foundation.loadPlayer(playerOpts);
	}

	private getQueryVariable(variable: string): string {
		const query = window.location.search.substring(1);
		const vars = query.split('&');

		for (let i = 0; i < vars.length; i++) {
			const pair = vars[i].split('=');

			if (decodeURIComponent(pair[0]) === variable) {
				return decodeURIComponent(pair[1]);
			}
		}
	}
}
